import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/context/AuthContext";
import { AUTH_IDS, NAV } from "@/constants/testIds";
import { formatApiErrorDetail } from "@/lib/api";
import { Ticket, Sparkles } from "lucide-react";
import { Logo } from "@/components/Logo";

export default function Register() {
  const [form, setForm] = useState({ name: "", venue_name: "", email: "", password: "", country: "GB", account_type: "venue" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isSwitching = params.get("switch") === "1";
  const switchCreditStr = form.country === "US" ? "$500" : "£500";

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!acceptTerms) {
      setError("Please accept the Terms of Service and confirm you've read the Privacy Policy.");
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Affra");
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-[0.06]" aria-hidden="true" />
        <Link to="/" className="flex items-center gap-2 font-display font-black text-xl relative" data-testid={NAV.logo}>
          <span className="w-8 h-8 grid place-items-center bg-white text-zinc-900 rounded-md">
            <Ticket className="w-4 h-4" />
          </span>
          <span className="tracking-tight">Affra<span className="text-indigo-400">.</span></span>
        </Link>
        <div className="relative">
          <div className="font-display font-black text-5xl leading-tight tracking-tighter">Your venue.<br />Your brand.<br />Your tickets.</div>
          <p className="mt-4 text-zinc-400 max-w-md">Free forever. No card needed. No lock-in.</p>
        </div>
        <div className="text-xs text-zinc-500 relative">© {new Date().getFullYear()} Affra</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex mb-8" data-testid={`${NAV.logo}-form`}>
            <Logo size="h-24" />
          </Link>
          <div className="mb-8">
            <h1 className="font-display font-black text-3xl tracking-tight">
              {isSwitching ? "Switch To Us" : "Create your account"}
            </h1>
            <p className="mt-2 text-zinc-500 text-sm">
              {isSwitching ? "Sign up and we'll take it from here — migration, Stripe setup and launch-day cover." : "Start selling tickets in minutes."}
            </p>
          </div>

          {isSwitching && (
            <div
              data-testid="register-switch-banner"
              className="mb-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 flex items-start gap-3"
            >
              <span className="w-8 h-8 shrink-0 grid place-items-center rounded-lg bg-emerald-600 text-white">
                <Sparkles className="w-4 h-4" />
              </span>
              <div className="text-sm text-emerald-900">
                <div className="font-bold">You&apos;re on the Founding Partner track</div>
                <div className="mt-0.5 text-emerald-800">
                  Sign up and we&apos;ll migrate your events, help set up Stripe and support your first live event — up to <strong>{switchCreditStr}</strong> back as a Founding Partner.
                </div>
              </div>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4" data-testid={AUTH_IDS.registerForm}>
            <div>
              <Label>I&apos;m signing up as a…</Label>
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid="register-account-type">
                {[
                  ["venue", "Venue", "One fixed location"],
                  ["promoter", "Promoter", "Events at many locations"],
                ].map(([val, title, sub]) => {
                  const active = form.account_type === val;
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => setForm({ ...form, account_type: val })}
                      data-testid={`register-account-type-${val}`}
                      className={`text-left rounded-lg border p-3 transition ${
                        active ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 hover:border-zinc-300"
                      }`}
                    >
                      <div className="font-bold text-sm">{title}</div>
                      <div className={`text-xs mt-0.5 ${active ? "text-zinc-300" : "text-zinc-500"}`}>{sub}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" value={form.name} onChange={change("name")} required data-testid={AUTH_IDS.registerName} className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="venue_name">
                {form.account_type === "promoter" ? "Promoter / company name" : "Venue / organisation name"}
              </Label>
              <Input id="venue_name" value={form.venue_name} onChange={change("venue_name")} required data-testid={AUTH_IDS.registerVenueName} className="mt-2 h-11" />
            </div>
            <div>
              <Label>{form.account_type === "promoter" ? "Where are you based?" : "Where is your venue based?"}</Label>
              <div className="mt-2 grid grid-cols-2 gap-2" data-testid="register-country">
                {[
                  ["GB", "🇬🇧", "United Kingdom", "prices in £"],
                  ["US", "🇺🇸", "United States", "prices in $"],
                ].map(([code, flag, label, hint]) => {
                  const active = form.country === code;
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, country: code }))}
                      data-testid={`register-country-${code}`}
                      className={`text-left rounded-lg border px-3 py-2.5 transition ${
                        active ? "border-zinc-900 bg-zinc-50 ring-2 ring-zinc-900" : "border-zinc-200 hover:border-zinc-400"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg leading-none">{flag}</span>
                        <span className="text-sm font-semibold">{label}</span>
                      </div>
                      <div className="mt-0.5 text-[11px] text-zinc-500">{hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={change("email")} required data-testid={AUTH_IDS.registerEmail} className="mt-2 h-11" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={form.password} onChange={change("password")} required minLength={6} data-testid={AUTH_IDS.registerPassword} className="mt-2 h-11" />
              <div className="mt-1 text-xs text-zinc-500">At least 6 characters.</div>
            </div>
            {error && <div className="text-sm text-red-600" data-testid={AUTH_IDS.error}>{error}</div>}
            <div className="flex items-start gap-2.5 pt-1">
              <Checkbox
                id="accept-terms"
                checked={acceptTerms}
                onCheckedChange={(v) => setAcceptTerms(!!v)}
                data-testid="register-accept-terms"
                className="mt-0.5"
              />
              <Label htmlFor="accept-terms" className="text-sm text-zinc-600 font-normal leading-relaxed cursor-pointer">
                I agree to the{" "}
                <Link to="/terms" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">Terms of Service</Link>
                {" "}and confirm I have read the{" "}
                <Link to="/privacy" target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">Privacy Policy</Link>.
              </Label>
            </div>
            <Button type="submit" disabled={loading || !acceptTerms} data-testid={AUTH_IDS.registerSubmit} className="w-full h-11 rounded-full bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <div className="mt-6 text-sm text-zinc-500">
            Have an account?{" "}
            <Link to="/login" className="text-indigo-600 font-medium hover:underline">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
